// ICIC_NOTIFICATIONS_V1
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { FCM } from '@capacitor-community/fcm';
import { getNativeDeviceToken } from './nativeDeviceAuth';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

export const NOTIFICATION_TYPES = [
  { key: 'course_notifications', label: 'Course Notifications' },
  { key: 'course_registration', label: 'Honors Course Registration' },
  { key: 'critical_questions', label: 'Critical Questions' },
  { key: 'philosophical_diary', label: 'Philosophical Diary' },
  { key: 'assignments', label: 'Assignments' },
  { key: 'chat_messages', label: 'Chat Messages' },
  { key: 'class_sessions', label: 'Class' },
  { key: 'self_evaluation_forms', label: 'Self-Evaluation Forms' },
  { key: 'self_evaluation_meetings', label: 'Self-Evaluation Meetings' },
  { key: 'instructor_meetings', label: 'Instructor Meetings' },
  { key: 'events', label: 'Honors Events' },
  { key: 'icic', label: 'ICIC Conference' }
];

const CHANNELS = [
  ['course_notifications', 'Course Notifications', 'Course notifications from instructors'],
  ['course_registration', 'Honors Course Registration', 'Honors course registration and recommendation notifications'],
  ['critical_questions', 'Critical Questions', 'Critical Question notifications'],
  ['philosophical_diary', 'Philosophical Diary', 'Philosophical Diary notifications'],
  ['assignments', 'Assignments', 'Assignment notifications'],
  ['chat_messages', 'Chat Messages', 'Chat message notifications'],
  ['class_sessions', 'Class', 'Notifications when a class session takes place'],
  ['self_evaluation_forms', 'Self-Evaluation Forms', 'Self-Evaluation Form notifications'],
  ['self_evaluation_meetings', 'Self-Evaluation Meetings', 'Self-Evaluation Meeting notifications'],
  ['instructor_meetings', 'Instructor Meetings', 'Reminders for meetings scheduled with an instructor'],
  ['honors_events', 'Honors Events', 'Honors event invitations and reminders'],
  ['icic_conference', 'ICIC Conference', 'ICIC poster, judging, voting and award notifications']
];

function apiHeaders(extra = {}) {
  return {
    apikey: SUPABASE_KEY,
    Authorization: 'Bearer ' + SUPABASE_KEY,
    'Content-Type': 'application/json',
    ...extra
  };
}

async function rpc(name, body, label) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/rpc/${name}`,
    {
      method: 'POST',
      headers: apiHeaders(),
      body: JSON.stringify(body)
    }
  );

  const text = await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text || response.statusText}`
    );
  }

  return text ? JSON.parse(text) : null;
}

async function currentDeviceToken() {
  const token = await getNativeDeviceToken();

  if (!token) {
    throw new Error('This device does not have a saved student-app login.');
  }

  return token;
}

export async function getStudentNotificationPreferences(studentId) {
  const deviceToken = await currentDeviceToken();

  const result = await rpc(
    'student_get_notification_preferences',
    {
      p_student_id: studentId,
      p_device_token: deviceToken
    },
    'Loading notification preferences'
  );

  if (!result?.ok || !result?.preferences) {
    throw new Error(
      result?.status === 'not_authorised'
        ? 'This device is not authorised to change notification settings.'
        : 'Notification preferences could not be loaded.'
    );
  }

  return result.preferences;
}

export async function updateStudentNotificationPreferences(studentId, values) {
  const deviceToken = await currentDeviceToken();

  if (
    Object.prototype.hasOwnProperty.call(
      values || {},
      'icic'
    )
  ) {
    const result =
      await rpc(
        'student_update_icic_notification_preference',
        {
          p_student_id:
            studentId,

          p_device_token:
            deviceToken,

          p_enabled:
            values.icic !== false
        },
        'Saving ICIC Conference notification preference'
      );

    if (
      !result?.ok ||
      !result?.preferences
    ) {
      throw new Error(
        result?.status ===
          'not_authorised'
          ? 'This device is not authorised to change notification settings.'
          : 'Notification preferences could not be saved.'
      );
    }

    const otherValues =
      Object.fromEntries(
        Object.entries(
          values || {}
        ).filter(
          ([key]) =>
            key !== 'icic'
        )
      );

    if (
      Object.keys(
        otherValues
      ).length === 0
    ) {
      return result.preferences;
    }

    values =
      otherValues;
  }

  if (
    Object.prototype.hasOwnProperty.call(
      values || {},
      'course_registration'
    )
  ) {
    const courseRegistrationResult =
      await rpc(
        'student_update_course_registration_notification_preference',
        {
          p_student_id:
            studentId,

          p_device_token:
            deviceToken,

          p_enabled:
            values.course_registration !==
            false
        },
        'Saving Honors Course Registration notification preference'
      );


    if (
      !courseRegistrationResult?.ok ||
      !courseRegistrationResult?.preferences
    ) {
      throw new Error(
        courseRegistrationResult?.status ===
          'not_authorised'
          ? 'This device is not authorised to change notification settings.'
          : 'Notification preferences could not be saved.'
      );
    }


    const otherValues =
      Object.fromEntries(
        Object.entries(
          values || {}
        ).filter(
          ([key]) =>
            key !==
            'course_registration'
        )
      );


    if (
      Object.keys(
        otherValues
      ).length === 0
    ) {
      return courseRegistrationResult.preferences;
    }
  }


  const result = await rpc(
    'student_update_notification_preferences',
    {
      p_student_id: studentId,
      p_device_token: deviceToken,
      p_values: values
    },
    'Saving notification preferences'
  );

  if (!result?.ok || !result?.preferences) {
    throw new Error(
      result?.status === 'not_authorised'
        ? 'This device is not authorised to change notification settings.'
        : 'Notification preferences could not be saved.'
    );
  }

  return result.preferences;
}

export async function getPushPermissionState() {
  if (!Capacitor.isNativePlatform()) {
    return 'unsupported';
  }

  const status = await PushNotifications.checkPermissions();
  return status.receive;
}

async function createAndroidChannels() {
  if (Capacitor.getPlatform() !== 'android') return;

  for (const [id, name, description] of CHANNELS) {
    await PushNotifications.createChannel({
      id,
      name,
      description,
      importance: 3,
      visibility: 1
    });
  }
}

async function saveFcmToken(studentId, fcmToken) {
  const deviceToken = await currentDeviceToken();

  const result = await rpc(
    'student_register_push_token',
    {
      p_student_id: studentId,
      p_device_token: deviceToken,
      p_push_token: fcmToken,
      p_platform: Capacitor.getPlatform()
    },
    'Registering push notifications'
  );

  if (!result?.ok) {
    throw new Error(
      result?.status === 'not_authorised'
        ? 'This device is not authorised for push notifications.'
        : 'The push-notification token could not be registered.'
    );
  }

  return result;
}

async function getMessagingToken(registrationToken) {
  const platform = Capacitor.getPlatform();

  if (platform === 'android') {
    return registrationToken;
  }

  if (platform === 'ios') {
    const result = await FCM.getToken();
    return result?.token || '';
  }

  return '';
}

async function waitForRegistrationToken() {
  let registrationHandle = null;
  let errorHandle = null;
  let timer = null;

  try {
    let resolveToken;
    let rejectToken;

    const tokenPromise = new Promise((resolve, reject) => {
      resolveToken = resolve;
      rejectToken = reject;
    });

    registrationHandle = await PushNotifications.addListener(
      'registration',
      token => {
        getMessagingToken(token?.value || '')
          .then(resolveToken)
          .catch(rejectToken);
      }
    );

    errorHandle = await PushNotifications.addListener(
      'registrationError',
      error => rejectToken(
        new Error(
          error?.error ||
          error?.message ||
          'Could not register for push notifications.'
        )
      )
    );

    timer = window.setTimeout(() => {
      rejectToken(
        new Error('Timed out while registering for push notifications.')
      );
    }, 15000);

    await PushNotifications.register();

    const token = await tokenPromise;

    if (!token) {
      throw new Error(
        'The device did not return a push-notification token.'
      );
    }

    return token;
  } finally {
    if (timer) window.clearTimeout(timer);
    if (registrationHandle) await registrationHandle.remove();
    if (errorHandle) await errorHandle.remove();
  }
}

export async function ensureStudentPushRegistration(
  session,
  {
    askPermission = false,
    ignoreStoredPreference = false
  } = {}
) {
  const platform = Capacitor.getPlatform();

  if (
    !Capacitor.isNativePlatform() ||
    !['android', 'ios'].includes(platform) ||
    !session?.studentId ||
    session?.isMaster
  ) {
    return {
      supported: false,
      permission: 'unsupported',
      registered: false
    };
  }

  if (!ignoreStoredPreference) {
    try {
      const preferences = await getStudentNotificationPreferences(
        session.studentId
      );

      if (preferences?.push_enabled === false) {
        return {
          supported: true,
          permission: await getPushPermissionState(),
          registered: false,
          disabledByStudent: true
        };
      }
    } catch (err) {
      console.error('Could not pre-check notification preferences:', err);
    }
  }

  let permission = await PushNotifications.checkPermissions();

  if (permission.receive !== 'granted' && askPermission) {
    permission = await PushNotifications.requestPermissions();
  }

  if (permission.receive !== 'granted') {
    return {
      supported: true,
      permission: permission.receive,
      registered: false
    };
  }

  await createAndroidChannels();

  const fcmToken = await waitForRegistrationToken();
  await saveFcmToken(session.studentId, fcmToken);

  return {
    supported: true,
    permission: 'granted',
    registered: true
  };
}